import type BigNumber from 'bignumber.js';
import { poolLensAbi, primeAbi, vaiVaultAbi, venusLensAbi, xvsVaultAbi } from 'libs/contracts';
import { logError } from 'libs/errors';
import convertPriceMantissaToDollars from 'utilities/convertPriceMantissaToDollars';
import findTokenByAddress from 'utilities/findTokenByAddress';
import removeDuplicates from 'utilities/removeDuplicates';
import type { Address } from 'viem';
import formatOutput from './formatOutput';
import { getApiTokenPrice } from './getApiTokenPrice';
import { getMerklUserRewards } from './getMerklUserRewards';
import type {
  GetPendingRewardsInput,
  GetPendingRewardsOutput,
  PendingExternalRewardSummary,
} from './types';

type FormatOutputInput = Parameters<typeof formatOutput>[0];

type MulticallResult<T> =
  | {
      status: 'success';
      result: T;
    }
  | {
      status: 'failure';
    };

type PendingRewardsReadKey =
  | 'vaiVaultPendingXvs'
  | 'vaiVaultPaused'
  | 'venusLensPendingRewards'
  | 'isolatedPoolPendingRewards'
  | 'xvsVestingVaultPaused'
  | 'xvsVestingVaultPoolInfos'
  | 'xvsVestingVaultPendingReward'
  | 'xvsVestingVaultPendingWithdrawalsBeforeUpgrade'
  | 'primePaused'
  | 'primePendingRewards';

type PendingRewardsRead = {
  key: PendingRewardsReadKey;
  index?: number;
  contract: {
    abi: unknown;
    address: Address;
    functionName: string;
    args?: readonly unknown[];
  };
};

const getMulticallResult = <T>(result: MulticallResult<unknown> | undefined) =>
  result?.status === 'success' ? (result.result as T) : undefined;

export const getPendingRewards = async ({
  tokens,
  publicClient,
  legacyPoolComptrollerContractAddress,
  isolatedPoolComptrollerAddresses,
  xvsVestingVaultPoolCount,
  accountAddress,
  venusLensContractAddress,
  poolLensContractAddress,
  vaiVaultContractAddress,
  xvsVaultContractAddress,
  primeContractAddress,
  chainId,
  merklCampaigns,
}: GetPendingRewardsInput): Promise<GetPendingRewardsOutput> => {
  const xvsTokenAddress = tokens.find((token) => token.symbol === 'XVS')?.address;

  const pendingRewardsReads: PendingRewardsRead[] = [];

  if (vaiVaultContractAddress) {
    pendingRewardsReads.push(
      {
        key: 'vaiVaultPendingXvs',
        contract: {
          abi: vaiVaultAbi,
          address: vaiVaultContractAddress,
          functionName: 'pendingXVS',
          args: [accountAddress],
        },
      },
      {
        key: 'vaiVaultPaused',
        contract: {
          abi: vaiVaultAbi,
          address: vaiVaultContractAddress,
          functionName: 'vaultPaused',
        },
      },
    );
  }

  if (venusLensContractAddress && legacyPoolComptrollerContractAddress) {
    pendingRewardsReads.push({
      key: 'venusLensPendingRewards',
      contract: {
        abi: venusLensAbi,
        address: venusLensContractAddress,
        functionName: 'pendingRewards',
        args: [accountAddress, legacyPoolComptrollerContractAddress],
      },
    });
  }

  if (poolLensContractAddress) {
    isolatedPoolComptrollerAddresses.forEach((isolatedPoolComptrollerAddress, index) => {
      pendingRewardsReads.push({
        key: 'isolatedPoolPendingRewards',
        index,
        contract: {
          abi: poolLensAbi,
          address: poolLensContractAddress,
          functionName: 'getPendingRewards',
          args: [accountAddress, isolatedPoolComptrollerAddress],
        },
      });
    });
  }

  if (xvsVaultContractAddress && xvsTokenAddress) {
    pendingRewardsReads.push({
      key: 'xvsVestingVaultPaused',
      contract: {
        abi: xvsVaultAbi,
        address: xvsVaultContractAddress,
        functionName: 'vaultPaused',
      },
    });

    for (let poolIndex = 0; poolIndex < xvsVestingVaultPoolCount; poolIndex++) {
      pendingRewardsReads.push(
        {
          key: 'xvsVestingVaultPoolInfos',
          index: poolIndex,
          contract: {
            abi: xvsVaultAbi,
            address: xvsVaultContractAddress,
            functionName: 'poolInfos',
            args: [xvsTokenAddress, BigInt(poolIndex)],
          },
        },
        {
          key: 'xvsVestingVaultPendingReward',
          index: poolIndex,
          contract: {
            abi: xvsVaultAbi,
            address: xvsVaultContractAddress,
            functionName: 'pendingReward',
            args: [xvsTokenAddress, BigInt(poolIndex), accountAddress],
          },
        },
        {
          key: 'xvsVestingVaultPendingWithdrawalsBeforeUpgrade',
          index: poolIndex,
          contract: {
            abi: xvsVaultAbi,
            address: xvsVaultContractAddress,
            functionName: 'pendingWithdrawalsBeforeUpgrade',
            args: [xvsTokenAddress, BigInt(poolIndex), accountAddress],
          },
        },
      );
    }
  }

  if (primeContractAddress) {
    pendingRewardsReads.push(
      {
        key: 'primePaused',
        contract: {
          abi: primeAbi,
          address: primeContractAddress,
          functionName: 'paused',
        },
      },
      {
        key: 'primePendingRewards',
        contract: {
          abi: primeAbi,
          address: primeContractAddress,
          functionName: 'getPendingRewards',
          args: [accountAddress],
        },
      },
    );
  }

  const pendingRewardsReadResults = pendingRewardsReads.length
    ? ((await publicClient.multicall({
        contracts: pendingRewardsReads.map(({ contract }) => contract) as never,
      })) as unknown as MulticallResult<unknown>[])
    : [];

  let vaiVaultPendingXvsMantissa: FormatOutputInput['vaiVaultPendingXvsMantissa'];
  let venusLensPendingRewards: FormatOutputInput['venusLensPendingRewards'];
  let isVaiVaultContractPaused = false;
  let isXvsVestingVaultContractPaused = false;
  let isPrimeContractPaused = false;
  let primePendingRewards: FormatOutputInput['primePendingRewards'];
  const isolatedPoolsPendingRewardsResults: FormatOutputInput['isolatedPoolsPendingRewards'] =
    new Array(isolatedPoolComptrollerAddresses.length);
  const xvsVestingVaultPoolInfosResults: FormatOutputInput['xvsVestingVaultPoolInfos'] = new Array(
    xvsVestingVaultPoolCount,
  );
  const xvsVestingVaultPendingRewardResults: FormatOutputInput['xvsVestingVaultPendingRewards'] =
    new Array(xvsVestingVaultPoolCount);
  const xvsVestingVaultPendingWithdrawalsBeforeUpgradeResults: FormatOutputInput['xvsVestingVaultPendingWithdrawalsBeforeUpgrade'] =
    new Array(xvsVestingVaultPoolCount);

  pendingRewardsReads.forEach((read, index) => {
    const result = pendingRewardsReadResults[index];

    switch (read.key) {
      case 'vaiVaultPendingXvs':
        vaiVaultPendingXvsMantissa = getMulticallResult(result);
        break;
      case 'vaiVaultPaused':
        isVaiVaultContractPaused = getMulticallResult<boolean>(result) ?? false;
        break;
      case 'venusLensPendingRewards':
        venusLensPendingRewards = getMulticallResult(result);
        break;
      case 'isolatedPoolPendingRewards':
        isolatedPoolsPendingRewardsResults[read.index!] = getMulticallResult(result);
        break;
      case 'xvsVestingVaultPaused':
        isXvsVestingVaultContractPaused = getMulticallResult<boolean>(result) ?? false;
        break;
      case 'xvsVestingVaultPoolInfos':
        xvsVestingVaultPoolInfosResults[read.index!] = getMulticallResult(result);
        break;
      case 'xvsVestingVaultPendingReward':
        xvsVestingVaultPendingRewardResults[read.index!] = getMulticallResult(result);
        break;
      case 'xvsVestingVaultPendingWithdrawalsBeforeUpgrade':
        xvsVestingVaultPendingWithdrawalsBeforeUpgradeResults[read.index!] =
          getMulticallResult(result);
        break;
      case 'primePaused':
        isPrimeContractPaused = getMulticallResult<boolean>(result) ?? false;
        break;
      case 'primePendingRewards':
        primePendingRewards = getMulticallResult(result);
        break;
    }
  });

  // Only fetchMerkl rewards if there are campaigns
  const hasMerklCampaigns = Object.keys(merklCampaigns).length > 0;
  let merklPendingRewards: PendingExternalRewardSummary[] = [];

  if (hasMerklCampaigns) {
    const [merklApiResponseSettled] = await Promise.allSettled([
      getMerklUserRewards({
        merklCampaigns,
        chainId,
        accountAddress,
      }),
    ]);

    if (merklApiResponseSettled.status === 'rejected') {
      logError(merklApiResponseSettled.reason);
    } else {
      merklPendingRewards = merklApiResponseSettled.value;
    }
  }

  const isolatedPoolRewardTokenAddresses = isolatedPoolsPendingRewardsResults.reduce<string[]>(
    (acc, isolatedPoolsPendingRewards) => {
      if (!isolatedPoolsPendingRewards) {
        return acc;
      }

      const rewardTokenAddresses = isolatedPoolsPendingRewards.map(
        (rewardSummary) => rewardSummary.rewardTokenAddress,
      );

      return [...acc, ...rewardTokenAddresses];
    },
    [],
  );

  const formattedPrimePendingRewards = primePendingRewards || [];
  const primeRewardTokenAddresses = formattedPrimePendingRewards.map(
    (primePendingReward) => primePendingReward.rewardToken,
  );

  const merklRewardTokenAddresses = merklPendingRewards
    ? merklPendingRewards.map((r) => r.rewardTokenAddress)
    : [];

  const rewardTokenAddresses = removeDuplicates([
    ...(xvsTokenAddress ? [xvsTokenAddress] : []),
    ...isolatedPoolRewardTokenAddresses,
    ...primeRewardTokenAddresses,
    ...merklRewardTokenAddresses,
  ]);

  const tokenPriceMantissaMapping =
    rewardTokenAddresses.length > 0
      ? await getApiTokenPrice({
          tokenAddresses: rewardTokenAddresses,
          chainId,
        })
      : {};

  const tokenPriceMapping: Record<string, BigNumber> = Object.entries(
    tokenPriceMantissaMapping,
  ).reduce<{
    [address: string]: BigNumber;
  }>((acc, tokenPriceMantissaTuple) => {
    const rewardTokenAddress = tokenPriceMantissaTuple[0];
    const availableRewardTokenPrices = tokenPriceMantissaTuple[1];
    const referenceRewardTokenPrice =
      availableRewardTokenPrices.find((tokenPrice) => tokenPrice.priceSource === 'oracle') ||
      availableRewardTokenPrices.find((tokenPrice) => tokenPrice.priceSource === 'coingecko') ||
      availableRewardTokenPrices.find((tokenPrice) => tokenPrice.priceSource === 'merkl');
    const rewardToken = findTokenByAddress({
      tokens,
      address: rewardTokenAddress,
    });

    if (!rewardToken) {
      return acc;
    }

    if (!referenceRewardTokenPrice) {
      return acc;
    }

    const rewardTokenPriceDollars = convertPriceMantissaToDollars({
      priceMantissa: referenceRewardTokenPrice.priceMantissa,
      decimals: rewardToken.decimals,
    });

    return {
      ...acc,
      [rewardTokenAddress.toLowerCase()]: rewardTokenPriceDollars,
    };
  }, {});

  const pendingRewardGroups = formatOutput({
    tokens,
    legacyPoolComptrollerContractAddress,
    isolatedPoolComptrollerAddresses,
    vaiVaultPendingXvsMantissa,
    venusLensPendingRewards,
    isolatedPoolsPendingRewards: isolatedPoolsPendingRewardsResults,
    xvsVestingVaultPoolInfos: xvsVestingVaultPoolInfosResults,
    xvsVestingVaultPendingRewards: xvsVestingVaultPendingRewardResults,
    xvsVestingVaultPendingWithdrawalsBeforeUpgrade:
      xvsVestingVaultPendingWithdrawalsBeforeUpgradeResults,
    tokenPriceMapping,
    primePendingRewards: formattedPrimePendingRewards,
    isVaiVaultContractPaused,
    isXvsVestingVaultContractPaused,
    isPrimeContractPaused,
    merklPendingRewards,
  });

  return {
    pendingRewardGroups,
  };
};
